import update from "immutability-helper";
import type { AnyAction } from "redux";
import type { PrepSession } from "@theirstory/common/api_types/backend";
import type { DailyParticipant } from "@daily-co/daily-js";

import {
  DAILY_CALL_STATUS_CREATING,
  DAILY_CALL_STATUS_CREATED,
  DAILY_CALL_RESET_CURRENT,
  DAILY_CALL_SHOW_TESTIMONIAL_QUESTIONS,
  DAILY_CALL_STATUS_JOINING,
  DAILY_CALL_STATUS_JOINED,
  DAILY_CALL_STATUS_SET_ROOM_DATA,
  DAILY_CALL_RECORDING_STATE,
  DAILY_CALL_SIDE_PANEL_STATE,
  DAILY_UPDATE_INVITE_MODAL_STATE,
  DAILY_CALL_STATUS_SET_USER_DATA,
  DAILY_CALL_STATUS_JOINED_MEETING,
  DAILY_CALL_SHOW_RECORD_SETTINGS,
  DAILY_CALL_HIDE_PARTICIPANT_VIDEO,
  DAILY_CALL_UPDATE_PARTICIPANTS,
  DAILY_CALL_UPDATE_NUMBER_RECORDINGS,
} from "../actions/types";

export enum CallStatus {
  Idle = "IDLE",
  InitiatingRoomCreation = "INITIATING_ROOM_CREATION",
  RoomSuccessfullyCreated = "ROOM_SUCCESSFULLY_CREATED",
  GotRoomData = "GOT_ROOM_DATA",
  GotUserData = "GOT_USER_DATA",
  AttemptingRoomJoin = "ATTEMPTING_ROOM_JOIN",
  PreJoinModal = "IN_PRE_JOIN_MODAL",
  DeviceCheckModal = "IN_DEVICE_CHECK_MODAL",
  SuccessfullyInRoom = "SUCCESSFULLY_IN_ROOM",
}

export interface Question {
  createdAt: string;
  prepsession: string;
  story: string;
  suggested_question_id: string;
  title: string;
  user: string;
  _id: string;
}

export interface DailyCallState {
  dailyParticipants: DailyParticipant[];
  hideVideoParticipantsId: string[];
  callStatus: CallStatus;
  isCreator: boolean;
  token: string;
  roomUrl: string;
  isTestimonialQuestionsVisible: boolean;
  isRecordSettingsVisible: boolean;
  projectId: string;
  folderId: string;
  storyId: string;
  questions: Question[];
  isRecording: boolean;
  isPanelOpen: boolean;
  showInviteModal: boolean;
  prepsessionData: PrepSession;
  name: string;
  email: string;
  password: string;
  sessionPassword: string;
  termsAccepted: boolean;
  userDataCompleted: boolean;
  numberOfRecordings: number;
}

const initialState: DailyCallState = {
  dailyParticipants: undefined,
  hideVideoParticipantsId: [],
  callStatus: CallStatus.Idle,
  isCreator: false,
  token: null,
  roomUrl: null,
  isTestimonialQuestionsVisible: false,
  isRecordSettingsVisible: false,
  projectId: null,
  folderId: null,
  storyId: null,
  questions: [],
  isRecording: false,
  isPanelOpen: false,
  showInviteModal: false,
  prepsessionData: null,
  name: null,
  email: null,
  password: null,
  sessionPassword: null,
  termsAccepted: false,
  userDataCompleted: false,
  numberOfRecordings: 0,
};

const dailyCallReducer = (state = initialState, { type, payload }: AnyAction) => {
  switch (type) {
    case DAILY_CALL_STATUS_CREATING: {
      return update(state, { callStatus: { $set: CallStatus.InitiatingRoomCreation } });
    }
    case DAILY_CALL_STATUS_CREATED: {
      return update(state, {
        callStatus: { $set: CallStatus.RoomSuccessfullyCreated },
        projectId: { $set: payload.projectId },
        folderId: { $set: payload.folderId },
        storyId: { $set: payload.storyId },
        isCreator: { $set: payload.creator },
      });
    }

    case DAILY_CALL_STATUS_SET_ROOM_DATA: {
      const projectId = payload.projectId ?? state.projectId;
      const folderId = payload.folderId ?? state.folderId;

      return update(state, {
        token: { $set: payload.token },
        roomUrl: { $set: payload.roomUrl },
        isCreator: { $set: payload.isCreator },
        storyId: { $set: payload.storyId },
        projectId: { $set: projectId },
        folderId: { $set: folderId },
        questions: { $set: payload.questions },
        sessionPassword: { $set: payload.sessionPassword },
        prepsessionData: { $set: payload.prepsessionData },
        callStatus: { $set: CallStatus.PreJoinModal },
      });
    }

    case DAILY_CALL_STATUS_SET_USER_DATA: {
      return update(state, {
        name: { $set: payload.name },
        email: { $set: payload.email },
        password: { $set: payload.password },
        termsAccepted: { $set: payload.termsAccepted },
        userDataCompleted: { $set: true },
      });
    }

    case DAILY_CALL_STATUS_JOINING: {
      return update(state, { callStatus: { $set: CallStatus.AttemptingRoomJoin } });
    }

    case DAILY_CALL_STATUS_JOINED: {
      return update(state, {
        token: { $set: payload.token },
        roomUrl: { $set: payload.roomUrl },
        callStatus: { $set: CallStatus.DeviceCheckModal },
      });
    }

    case DAILY_CALL_STATUS_JOINED_MEETING: {
      return update(state, {
        callStatus: { $set: CallStatus.SuccessfullyInRoom },
      });
    }

    case DAILY_CALL_SHOW_TESTIMONIAL_QUESTIONS: {
      return update(state, {
        isTestimonialQuestionsVisible: { $set: payload.show },
      });
    }

    case DAILY_CALL_UPDATE_PARTICIPANTS: {
      return update(state, {
        dailyParticipants: { $set: payload.dailyParticipants },
      });
    }

    case DAILY_CALL_SHOW_RECORD_SETTINGS: {
      return update(state, {
        isRecordSettingsVisible: { $set: payload.show },
      });
    }

    case DAILY_CALL_RESET_CURRENT: {
      return initialState;
    }

    case DAILY_CALL_RECORDING_STATE: {
      return update(state, {
        isRecording: { $set: payload.isRecording },
      });
    }

    case DAILY_CALL_UPDATE_NUMBER_RECORDINGS: {
      return update(state, {
        numberOfRecordings: { $set: payload.numberOfRecordings },
      });
    }

    case DAILY_CALL_HIDE_PARTICIPANT_VIDEO: {
      return update(state, {
        hideVideoParticipantsId: { $set: payload.hideVideoParticipantsId },
      });
    }

    case DAILY_CALL_SIDE_PANEL_STATE: {
      return update(state, {
        isPanelOpen: { $set: payload.isPanelOpen },
      });
    }

    case DAILY_UPDATE_INVITE_MODAL_STATE: {
      return update(state, {
        showInviteModal: { $set: payload.showInviteModal },
      });
    }

    default:
      return state;
  }
};

export default dailyCallReducer;
